import React, { useCallback, useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import { IoCloseOutline } from "react-icons/io5";
import Popup from "reactjs-popup";
import API from "src/network";
import RestrictionMsgModal from "src/Components/RestrictionMsgModal";
import Post from "./Post";
import { CircularProgress } from "@material-ui/core";
import { isChildSayMessageType, updateItemSayCount } from "src/Utils/commonUtils";

function SearchPostPopup({
  open,
  onClose,
  tenantId,
  searchParam,
  onHashTagClick,
}) {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);
  const [list, setList] = useState([]);
  const [childSaysLoading, setChildSaysLoading] = useState(false);
  const [childSaysData, setChildSaysData] = useState({});
  const [followData, setFollowData] = useState({});
  const [voteUpData, setVoteUpData] = useState({});
  const [hasRestrictionSays, setHasRestrictionSays] = useState(false);
  const [hasRestrictionSaysStatus, setHasRestrictionSaysStatus] = useState("");
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const postRef = useRef(null);
  const listRef = useRef(null);

  const getSearchData = async (page, reset = false) => {
    try {
      if (!hasMore && page !== 1 && !reset) return;

      if (page === 1) {
        setLoading(true); // only first time
      } else {
        setLoadingMore(true); // scrolling
      }

      const offset = (page - 1) * 10;
      const response = await API.getSearchData(searchParam, offset);

      const resData = response?.data || {};
      const newList = response?.data?.messageList || [];

      setData(resData);
      if (page === 1 || reset) {
        setList(newList);
      } else {
        setList((prev) => [...prev, ...newList]);
      }

      if (newList.length < 10) {
        setHasMore(false);
      } else {
        setHasMore(true);
      }
    } catch (e) {
      console.log(e);
      setHasMore(false);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    setPageNumber(1);
    setHasMore(true);
    getSearchData(1, true);
  }, [searchParam]);

  useEffect(() => {
    if (pageNumber > 1) {
      getSearchData(pageNumber);
    }
  }, [pageNumber]);

  const refreshFeedData = useCallback(() => {
    setPageNumber(1);
    setHasMore(true);
    getSearchData(1, true);
  }, [searchParam]);

  const fetchChildSaysData = async (id, popCols, limit) => {
    setChildSaysLoading(true);
    try {
      const response = await API.fetchChildSaysData(id, popCols, limit);
      setChildSaysData(response.data);
    } catch (e) {
      console.error(e);
    } finally {
      setChildSaysLoading(false);
    }
  };

  const handleScroll = useCallback(() => {
    const { scrollTop, clientHeight, scrollHeight } = listRef.current;

    if (scrollTop + clientHeight >= scrollHeight - 5 && hasMore && !loadingMore) {
      setPageNumber((prevPageNumber) => prevPageNumber + 1);
    }
  }, [hasMore]);

  const postChildSayData = async (id, msg, lnglat, locSelect) => {
    try {
      setChildSaysLoading(true);
      const formdata = new FormData();
      formdata.append("Message", msg);
      const response = await API.postChildSayData(
        id,
        formdata,
        lnglat,
        locSelect,
      );
      const status = response?.data?.status || "";
      if (status === "New Hash") {
        return "New Hash";
      } else if (status === "E_Say" || status === "E") {
        setHasRestrictionSays(true);
        setHasRestrictionSaysStatus(status);
        return status;
      } else {
        await fetchChildSaysData(id);
        updateSaysCountInList(id, "add");
        return status;
      }
    } catch (e) {
      console.error(e);
    } finally {
      setChildSaysLoading(false);
    }
  };

  const fetchFollowData = async (id, status) => {
    try {
      const response = await API.fetchFollowUnfollowData(id, status);
      setFollowData(response.data);
    } catch (e) {
      console.error(e);
    }
  };

  const fetchVoteUpStatus = async (id, status) => {
    try {
      const response = await API.fetchVoteUpStatus(id, status);
      setVoteUpData(response.data);
    } catch (e) {
      console.error(e);
    }
  };

  const deleteMessage = async (id, type, Parent_ID) => {
    try {
      const response = await API.deleteMessage(id);
      if (response) {
        if (isChildSayMessageType(type)) {
          console.log("childSaysData", childSaysData);
          const updatedData = childSaysData.childSAYsMessageList.filter(
            (each) => each.id !== id,
          );
          setChildSaysData({
            ...childSaysData,
            childSAYsMessageList: updatedData,
          });

          updateSaysCountInList(Parent_ID, "minus");
        } else {
          const copiedList = [...list];
          setList(copiedList.filter((each) => each.id !== id));
        }
      }
    } catch (e) {
      console.error(e);
    }
  };

  const updateSaysCountInList = (id, operator) => {
    setList((prevList) => updateItemSayCount(prevList, id, operator));
  };

  return (
    <Popup
      modal
      key={"some-key"}
      open={open}
      onClose={onClose}
      contentStyle={{
        borderRadius: "10px",
        backgroundColor: "#E5E4E2",
        zIndex: 1200,
      }}
      overlayStyle={{
        zIndex: 1000,
      }}
      closeOnDocumentClick={false}
      closeOnEscape={false}
    >
      <div>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          pl={1.5}
          height={40}
        >
          <Typography
            id="modal-modal-title"
            variant="h6"
            component="h2"
            style={{ paddingLeft: "10px" }}
          >
            {!loading && data?.totalList !== undefined && data?.searchText && (
              <Typography
                id="modal-modal-title"
                variant="h6"
                component="h2"
                style={{ paddingLeft: "10px" }}
              >
                {data.totalList} Search results for <b>{data.searchText}</b>
              </Typography>
            )}
          </Typography>
          <IconButton
            onClick={onClose}
            style={{ margin: 0, padding: 4, outline: "none" }}
          >
            <IoCloseOutline />
          </IconButton>
        </Box>
        {loading ? (
          <div
            style={{
              marginBottom: 10,
              marginTop: 10,
              width: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <CircularProgress />
          </div>
        ) : (
          <div
            ref={listRef}
            onScroll={handleScroll}
            role="list"
            style={{ height: "85vh", overflowY: "auto" }}
          >
            {list.length > 0 ? (
              <>
                <Post
                  ref={postRef}
                  loading={childSaysLoading}
                  listdata={list}
                  data={data}
                  childSaysData={childSaysData}
                  fetchChildSaysData={fetchChildSaysData}
                  postChildSayData={postChildSayData}
                  followData={followData}
                  fetchFollowData={fetchFollowData}
                  fetchVoteUpStatus={fetchVoteUpStatus}
                  deleteMessage={deleteMessage}
                  fetchFeedData={refreshFeedData}
                  refreshFeedData={refreshFeedData}
                  showData="search"
                  appendId="PostSearch"
                  onHashTagClick={onHashTagClick}
                />
                {loadingMore && (
                  <div
                    style={{
                      marginBottom: 10,
                      marginTop: 10,
                      width: "100%",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <CircularProgress size={20}/>
                  </div>
                )}
              </>
            ) : (
              <p style={{ textAlign: "center", margin: "60px 0 60px 0" }}>
                No results found matching your criteria
              </p>
            )}
          </div>
        )}
      </div>
      {hasRestrictionSays && (
        <RestrictionMsgModal
          open={hasRestrictionSays}
          onClose={() => {
            setHasRestrictionSays(false);
            setHasRestrictionSaysStatus("");
          }}
          title="Thank you"
          message={`Your ${
            hasRestrictionSaysStatus === "E_Say" ? "Answer" : "Reply"
          } will publish as soon as the Community Moderator approves it.`}
        />
      )}
    </Popup>
  );
}

export default SearchPostPopup;
